import React from "react";

import {
	faArrowRight, // faSitemap,
	// faUserCheck,
	faUsers,
} from "@performyard/fontawesome/pro-regular-svg-icons";
import { Icon, Spinner, Text, TextDate } from "@performyard/py-components";

import { useCompensationWizardData } from "../types";

import { useSelectionSalarySummary } from "../../../../api/hooks/defs/compensation";

import { formatCurrency, formatCurrencyFull } from "../../utils";

type ReviewLaunchStepProps = {
	userSelectionId: string;
};

const ReviewLaunchStep = ({ userSelectionId }: ReviewLaunchStepProps) => {
	const { data: salaryData, isLoading: isSalaryLoading } = useSelectionSalarySummary(userSelectionId);
	const wizardData = useCompensationWizardData();
	const cycleDetails = wizardData["cycle-details"];
	const allotmentBudget = wizardData["cycle-budget"];
	const allotmentIncreaseFixed = allotmentBudget?.allotmentIncreaseFixed ?? 0;

	if (isSalaryLoading) {
		return (
			<div className="flex items-center justify-center py-12">
				<Spinner />
			</div>
		);
	}

	const currentTotal = salaryData?.totalSalary ?? 0;
	const employeeCount = salaryData?.employeeCount ?? 0;
	const percentChange = Math.round((allotmentIncreaseFixed / currentTotal) * 100);
	const newTotal = currentTotal + allotmentIncreaseFixed;

	return (
		<div className="mx-auto flex max-w-[619px] flex-col gap-8">
			<div className="text-center">
				<Text size="sub-heading" weight="semi-bold">
					Create a Compensation Cycle
				</Text>
				<Text size="paragraph-2" weight="regular">
					Preview & Finalize
				</Text>
			</div>

			<div className="flex flex-col gap-6 rounded-1 border border-solid border-neutral-2 p-6">
				<div>
					<Text size="paragraph-1" weight="semi-bold">
						{cycleDetails?.name}
					</Text>
					<div className="flex items-center gap-2">
						<div className="flex items-center gap-1">
							<Text size="paragraph-3" weight="semi-bold" color="neutral-7">
								Start Date:
							</Text>
							{cycleDetails?.startDate ? (
								<TextDate
									date={cycleDetails.startDate}
									hasTime
									compactTime
									dateSize="medium"
									color="neutral-7"
								/>
							) : (
								<Text size="paragraph-3" weight="regular" color="neutral-7">
									N/A
								</Text>
							)}
						</div>
						<div className="flex items-center gap-1">
							<Text size="paragraph-3" weight="semi-bold" color="neutral-7">
								Due Date:
							</Text>
							{cycleDetails?.dueDate ? (
								<TextDate
									date={cycleDetails.dueDate}
									hasTime
									compactTime
									dateSize="medium"
									color="neutral-7"
								/>
							) : (
								<Text size="paragraph-3" weight="regular" color="neutral-7">
									N/A
								</Text>
							)}
						</div>
					</div>
				</div>
				<div className="grid grid-cols-2 gap-2">
					{/* <div className="flex flex-col gap-2">
                        <Text size="paragraph-3" weight="semi-bold">
                            Approval Workflow
                        </Text>
                        <div className="flex items-center gap-2">
                            {wizardData?.["approval-chain"]?.approvalType === "manager" ? (
                                <Icon icon={faUserCheck} color="primary-6" size={16} />
                            ) : (
                                <Icon icon={faSitemap} color="primary-6" size={16} />
                            )}
                            <Text size="paragraph-3" weight="regular">
                                {wizardData?.["approval-chain"]?.approvalType === "manager"
                                    ? "Single Approval"
                                    : "Chain of Command"}
                            </Text>
                        </div>
                    </div> */}

					<div className="flex flex-col gap-2">
						<Text size="paragraph-3" weight="semi-bold">
							Cycle Employees
						</Text>
						<div className="flex items-center gap-2">
							<Icon icon={faUsers} color="primary-6" size={16} />
							<Text size="paragraph-3" weight="regular">
								{employeeCount} Employees
							</Text>
						</div>
					</div>
				</div>
				<div className="flex flex-col gap-2">
					<Text size="paragraph-3" weight="semi-bold">
						Cycle Budget
					</Text>
					<div className="w-full rounded-1 bg-neutral-1">
						<div className="flex flex-col gap-6 p-4">
							<div className="flex items-center justify-start gap-6">
								<div>
									<Text size="caption" weight="regular">
										Current Total
									</Text>
									<Text size="heading-3" weight="semi-bold">
										{formatCurrency(currentTotal)}
									</Text>
								</div>
								<Icon icon={faArrowRight} color="neutral-9" size={24} />
								<div>
									<Text size="caption" weight="regular">
										New Total
									</Text>
									<Text size="heading-3" weight="semi-bold">
										{formatCurrency(newTotal)}
									</Text>
								</div>
							</div>

							<div className="flex flex-col gap-2">
								<div className="flex items-center justify-between gap-2">
									<Text size="caption" weight="semi-bold">
										Current Total
									</Text>
									<Text size="caption" weight="regular">
										{formatCurrencyFull(currentTotal)}
									</Text>
								</div>
								<div className="h-[1px] bg-neutral-2" />
								<div className="flex items-center justify-between gap-2">
									<Text size="caption" weight="semi-bold">
										Budgeted Increase
									</Text>
									<div className="flex gap-2">
										<Text
											size="caption"
											weight="semi-bold"
											color="green-5"
										>{`+${percentChange}%`}</Text>
										<Text size="caption" weight="regular">
											{formatCurrencyFull(allotmentIncreaseFixed)}
										</Text>
									</div>
								</div>

								<div className="flex items-center justify-between gap-2">
									<Text size="caption" weight="semi-bold">
										New Total
									</Text>
									<Text size="caption" weight="regular">
										{formatCurrencyFull(currentTotal + allotmentIncreaseFixed)}
									</Text>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div></div>
			</div>
		</div>
	);
};

export default ReviewLaunchStep;
